import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PractitionerCredentialsForm, PractitionerCredentials } from './PractitionerCredentialsForm';
import type { Permission } from './PermissionMatrix';
import { ExplicitGrantsEditor } from './ExplicitGrantsEditor';
import { Building2, Users, Info } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '@/hooks/useAuth';

/**
 * Richer invitation shape used by edit-invitation mode.
 * Matches what the backend stores and returns for a pending invitation.
 */
export interface EditableInvitation {
  id: string;
  email: string;
  role: string;
  organizationId?: string;
  organizationName?: string;
  enterpriseId?: string;
  partnerId?: string;
  status?: 'pending' | 'accepted' | 'cancelled' | 'expired';
  expiresAt?: string;
  createdAt?: string;
  metadata?: {
    firstName?: string;
    lastName?: string;
    title?: string;
    department?: string;
    phone?: string;
    medicalLicense?: string;
    npi?: string;
    specialty?: string;
    specialties?: string[];
    licenseNumber?: string;
    deaNumber?: string;
    licenseExpiration?: string;
    externalId?: string;
    enterpriseId?: string;
    partnerId?: string;
  };
}

function buildInvitationDiff(inv: EditableInvitation, form: any): Record<string, any> {
  const diff: Record<string, any> = {};
  if (form.email !== inv.email) diff.email = form.email;
  if (form.role  !== inv.role)  diff.role  = form.role;
  const meta: Record<string, any> = {};
  if (form.firstName  !== (inv.metadata?.firstName  ?? '')) meta.firstName  = form.firstName;
  if (form.lastName   !== (inv.metadata?.lastName   ?? '')) meta.lastName   = form.lastName;
  if (form.profile?.title       !== (inv.metadata?.title ?? '')) meta.title = form.profile?.title;
  if (form.profile?.department  !== (inv.metadata?.department ?? '')) meta.department = form.profile?.department;
  if (form.profile?.phoneNumber !== (inv.metadata?.phone ?? '')) meta.phone = form.profile?.phoneNumber;
  const oldSpecialties = Array.isArray(inv.metadata?.specialties)
    ? inv.metadata.specialties
    : inv.metadata?.specialty ? [inv.metadata.specialty] : [];
  const newSpecialties: string[] = Array.isArray(form.profile?.specialties) ? form.profile.specialties : [];
  const specialtiesChanged = oldSpecialties.length !== newSpecialties.length
    || oldSpecialties.some((s, i) => s !== newSpecialties[i]);
  if (specialtiesChanged) meta.specialties = newSpecialties;
  if (form.practitioner?.medicalLicense !== (inv.metadata?.medicalLicense ?? '')) meta.medicalLicense = form.practitioner?.medicalLicense;
  if (form.practitioner?.npi            !== (inv.metadata?.npi            ?? '')) meta.npi            = form.practitioner?.npi;
  if (form.practitioner?.specialty      !== (inv.metadata?.specialty      ?? '')) meta.specialty      = form.practitioner?.specialty;
  if (form.practitioner?.licenseNumber  !== (inv.metadata?.licenseNumber  ?? '')) meta.licenseNumber  = form.practitioner?.licenseNumber;
  if (form.practitioner?.deaNumber         !== (inv.metadata?.deaNumber         ?? '')) meta.deaNumber         = form.practitioner?.deaNumber;
  if (form.practitioner?.licenseExpiration !== (inv.metadata?.licenseExpiration ?? '')) meta.licenseExpiration = form.practitioner?.licenseExpiration;
  if (form.enterpriseId !== (inv.enterpriseId ?? inv.metadata?.enterpriseId ?? '')) meta.enterpriseId = form.enterpriseId;
  if (form.partnerId    !== (inv.partnerId    ?? inv.metadata?.partnerId    ?? '')) meta.partnerId    = form.partnerId;
  if (form.externalId   !== (inv.metadata?.externalId ?? '')) meta.externalId = form.externalId;
  if (Object.keys(meta).length) diff.metadata = meta;
  return diff;
}

// User type - compatible with backend model
export type UserRole = 'superadmin' | 'admin' | 'operator' | 'staff'
  | 'enterprise_admin' | 'enterprise_user'
  | 'channel_partner_admin' | 'channel_partner_user';

export interface User {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  organizationId?: string;
  organizationName?: string; // Populated by backend for display
  enterpriseId?: string;
  partnerId?: string;
  permissions?: string[];
  profile?: {
    title?: string;
    department?: string;
    licenseNumber?: string;
    specialties?: string[];
    phoneNumber?: string;
  };
  practitioner?: PractitionerCredentials;
  preferences?: {
    emailNotifications: boolean;
    language: string;
    autoStartRecording: boolean;
    defaultAudioQuality: 'low' | 'medium' | 'high';
  };
  isActive?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  deactivationReason?: 'manual' | 'org_deleted' | 'security' | null;
  lastLogin?: string;
  createdAt?: string;
  externalId?: string;
  lastEhrSync?: Date;
}

interface OrganizationOption {
  id: string;
  name: string;
  partnerId?: string;
  settings?: {
    operationMode?: 'standalone' | 'connected';
    externalSystems?: Array<{
      systemId: string;
      displayName: string;
      status: 'active' | 'inactive' | 'error';
      isPrimary: boolean;
    }>;
  };
}

interface Partner {
  id: string;
  name: string;
  type: 'reseller' | 'affiliate' | 'enterprise' | 'internal';
  status: 'active' | 'suspended' | 'pending' | 'cancelled';
  isOwnerPartner: boolean;
}

interface EnterpriseOption {
  id: string;
  name: string;
}

interface UserModalProps {
  mode: 'create' | 'edit' | 'edit-invitation';
  user?: User | null;
  invitation?: EditableInvitation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (user: User) => Promise<void>;
  organizations: OrganizationOption[];
  enterprises?: EnterpriseOption[];
  partners?: Partner[];
}

const emptyUser: User = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'staff',
  permissions: [],
  profile: {
    title: '',
    department: '',
    licenseNumber: '',
    specialties: [],
    phoneNumber: ''
  },
  preferences: {
    emailNotifications: true,
    language: 'en-US',
    autoStartRecording: false,
    defaultAudioQuality: 'medium'
  },
  isActive: true
};

// Valid email domains for superadmin
const SUPERADMIN_DOMAINS = ['@harriscomputer.com', '@caretracker.com'];

const isValidSuperadminEmail = (email: string): boolean => {
  const lowerEmail = email.toLowerCase();
  return SUPERADMIN_DOMAINS.some(domain => lowerEmail.endsWith(domain));
};

const ENTERPRISE_ROLES: UserRole[] = ['enterprise_admin', 'enterprise_user'];
const PARTNER_ROLES: UserRole[] = ['channel_partner_admin', 'channel_partner_user'];
const ORG_ROLES: UserRole[] = ['admin', 'operator', 'staff'];

export const UserModal: React.FC<UserModalProps> = ({
  mode,
  user,
  invitation,
  open,
  onOpenChange,
  onSave,
  organizations,
  enterprises = [],
  partners: externalPartners,
}) => {
  const { isSuperAdmin, hasPermission, user: currentUser } = useAuth();
  // The partner list is a permission-gated endpoint a channel partner admin does not hold, so the
  // dropdown it feeds can never populate for that role. It has exactly one partner, its own, which
  // the token already carries.
  const canSelectPartner = hasPermission('platform.partners.view');
  const shouldReduceMotion = useReducedMotion();
  const [formData, setFormData] = useState<User>(emptyUser);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [activeTab, setActiveTab] = useState('identity');
  const [practitionerValid, setPractitionerValid] = useState(true);
  const [practitionerErrors, setPractitionerErrors] = useState<string[]>([]);
  const [showEmailDomainError, setShowEmailDomainError] = useState(false);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  // Partner state for filtering organizations
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('');
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);

  // Deep merge utility to ensure all fields exist even if missing from DB
  const deepMerge = (defaults: any, data: any): any => {
    if (!data) return defaults;

    const result = { ...defaults };

    for (const key in data) {
      if (data[key] !== null && typeof data[key] === 'object' && !Array.isArray(data[key])) {
        result[key] = deepMerge(defaults[key] || {}, data[key]);
      } else {
        result[key] = data[key];
      }
    }

    return result;
  };

  // Fetch partners on mount
  useEffect(() => {
    const fetchPartners = async () => {
      setIsLoadingPartners(true);
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/v1/platform/partners/search?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          setPartners(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch partners:', error);
      } finally {
        setIsLoadingPartners(false);
      }
    };

    if (open && canSelectPartner) {
      fetchPartners();
    }
  }, [open, canSelectPartner]);

  // Fetch permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/v1/roles/permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.success && data.data) {
          setPermissions(Array.isArray(data.data) ? data.data : data.data.permissions || []);
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
      }
    };

    if (open) {
      fetchPermissions();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (mode === 'edit-invitation' && invitation) {
        setFormData(deepMerge(emptyUser, {
          email: invitation.email,
          firstName: invitation.metadata?.firstName ?? '',
          lastName:  invitation.metadata?.lastName  ?? '',
          role: (invitation.role as UserRole) || 'staff',
          profile: {
            title:       invitation.metadata?.title ?? '',
            department:  invitation.metadata?.department ?? '',
            phoneNumber: invitation.metadata?.phone ?? '',
            specialties: Array.isArray(invitation.metadata?.specialties)
              ? invitation.metadata.specialties
              : invitation.metadata?.specialty ? [invitation.metadata.specialty] : [],
          },
          organizationId: invitation.organizationId ?? '',
          enterpriseId:   invitation.enterpriseId   ?? invitation.metadata?.enterpriseId ?? '',
          partnerId:      invitation.partnerId       ?? invitation.metadata?.partnerId    ?? '',
          practitioner: {
            medicalLicense:    invitation.metadata?.medicalLicense    ?? '',
            npi:               invitation.metadata?.npi               ?? '',
            specialty:         invitation.metadata?.specialty         ?? '',
            licenseNumber:     invitation.metadata?.licenseNumber     ?? '',
            deaNumber:         invitation.metadata?.deaNumber         ?? '',
            licenseExpiration: invitation.metadata?.licenseExpiration ?? '',
          },
          externalId: invitation.metadata?.externalId ?? '',
        }));
      } else {
        setFormData(user ? deepMerge(emptyUser, user) : emptyUser);
      }
      setError(null);
      setActiveTab('identity');
      setShowEmailDomainError(false);
      setPractitionerValid(true);
      setSelectedPartnerId('');
    }
  }, [open, mode, user, invitation]);

  // Declared after the effect that populates the form on open, so it lands on top of that reset.
  // Without it a partner admin could not complete a partner-role invite at all: the only control
  // that sets partnerId is a dropdown fed by an endpoint it is refused, and the server requires the
  // id. An existing value is left alone, so editing a user keeps its own partner.
  // Depends on the role as well as on open: the role selector clears partnerId when it moves to an
  // organization or enterprise role, so a switch back to a partner role has to refill it.
  useEffect(() => {
    if (!open || canSelectPartner || !currentUser?.partnerId) return;
    if (!PARTNER_ROLES.includes(formData.role)) return;
    setFormData(prev => (prev.partnerId ? prev : { ...prev, partnerId: currentUser.partnerId }));
  }, [open, canSelectPartner, currentUser?.partnerId, formData.role]);

  // Validate email domain for superadmin in real-time
  useEffect(() => {
    if (formData.role === 'superadmin') {
      const isValid = isValidSuperadminEmail(formData.email);
      setShowEmailDomainError(!isValid && formData.email.length > 0);
    } else {
      setShowEmailDomainError(false);
    }
  }, [formData.role, formData.email]);

  // Filter organizations by selected partner
  const filteredOrganizations = useMemo(() => {
    if (!selectedPartnerId) return organizations;
    return organizations.filter(org => org.partnerId === selectedPartnerId);
  }, [organizations, selectedPartnerId]);

  // Reset organization when partner changes
  useEffect(() => {
    if (selectedPartnerId && formData.organizationId) {
      const orgExists = filteredOrganizations.some(org => org.id === formData.organizationId);
      if (!orgExists) {
        setFormData(prev => ({ ...prev, organizationId: '' }));
      }
    }
  }, [selectedPartnerId, filteredOrganizations]);

  // External ID field is always shown when an organization is selected
  const showExternalIdField = !!formData.organizationId;

  // Role-based visibility
  const isEnterpriseRole = ENTERPRISE_ROLES.includes(formData.role);
  const isPartnerRole = PARTNER_ROLES.includes(formData.role);
  const isOrgRole = ORG_ROLES.includes(formData.role);
  const showOrgAssignment = isOrgRole;
  const showEnterpriseAssignment = isEnterpriseRole;
  const showPartnerAssignment = isPartnerRole;
  const showPractitionerForm = formData.role === 'operator';
  const showProfileTab = isOrgRole;

  // Use external partners if provided, otherwise use locally fetched ones
  const availablePartners = externalPartners || partners;

  const handleExternalIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      externalId: value || undefined
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Validate email domain for superadmin
      if (formData.role === 'superadmin' && !isValidSuperadminEmail(formData.email)) {
        throw new Error('Superadmin email must be from @harriscomputer.com or @caretracker.com domain');
      }

      // Validate assignment based on role
      if (ENTERPRISE_ROLES.includes(formData.role) && !formData.enterpriseId) {
        throw new Error('Enterprise is required for enterprise roles');
      }
      if (PARTNER_ROLES.includes(formData.role) && !formData.partnerId) {
        throw new Error('Channel Partner is required for partner roles');
      }
      if (ORG_ROLES.includes(formData.role) && !formData.organizationId) {
        throw new Error('Organization is required for clinical roles');
      }

      // Validate practitioner credentials for operator role
      if (formData.role === 'operator' && !practitionerValid) {
        throw new Error('Operator role requires valid practitioner credentials');
      }

      if (formData.role === 'operator' && !formData.practitioner?.medicalLicense) {
        throw new Error('Operator role requires medical license');
      }

      // edit-invitation mode: call invitations service instead of user save
      if (mode === 'edit-invitation' && invitation) {
        const { updateInvitation } = await import('../../services/invitations.service');
        const diff = buildInvitationDiff(invitation, formData);
        const result = await updateInvitation(invitation.id, diff);
        // TODO(Task 10): type result properly
        onSave?.(result.invitation as any);
        onOpenChange(false);
        return;
      }

      await onSave(formData);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save user');
    } finally {
      setIsLoading(false);
    }
  };

  const addSpecialty = () => {
    if (specialtyInput.trim()) {
      const updatedSpecialties = [...(formData.profile?.specialties || []), specialtyInput.trim()];
      setFormData({
        ...formData,
        profile: {
          ...formData.profile!,
            specialties: updatedSpecialties
        }
      });
      setSpecialtyInput('');
    }
  };

  const removeSpecialty = (index: number) => {
    setFormData({
      ...formData,
      profile: {
        ...formData.profile!,
        specialties: formData.profile?.specialties?.filter((_, i) => i !== index)
      }
    });
  };

  // Tab transition animation variants
  const tabContentVariants = shouldReduceMotion
    ? undefined
    : {
        initial: { opacity: 0, x: -10 },
        animate: { opacity: 1, x: 0 },
        exit: { opacity: 0, x: 10 }
      };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 grid grid-rows-[auto_auto_1fr_auto]">
        {/* Header with gradient accent */}
        <div className="border-b px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-purple-600" />
              {mode === 'create' ? 'Create New User' : mode === 'edit-invitation' ? 'Edit Pending Invitation' : 'Edit User'}
            </DialogTitle>
          </DialogHeader>
        </div>

        <form onSubmit={handleSubmit} className="contents">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="contents">
            {/* Tab Navigation - 2 tabs, second hidden for superadmin */}
            <div className="border-b px-6">
              <TabsList className={`grid w-full ${showProfileTab ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <TabsTrigger value="identity">Identity & Role</TabsTrigger>
                {showProfileTab && (
                  <TabsTrigger value="profile">Profile & Settings</TabsTrigger>
                )}
              </TabsList>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto px-6 py-6">
              {/* Tab 1: Identity & Role */}
              <TabsContent value="identity" className="mt-0">
                <motion.div
                  key="identity-tab"
                  variants={tabContentVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  {/* Identity Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      Identity
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="user-modal-first-name" className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <Input
                          id="user-modal-first-name"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          required
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="user-modal-last-name" className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                        <Input
                          id="user-modal-last-name"
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          required
                          placeholder="Doe"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label htmlFor="user-modal-email" className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <Input
                        id="user-modal-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        placeholder="john.doe@example.com"
                        className={showEmailDomainError ? 'border-red-500 focus:ring-red-500' : ''}
                      />
                      {formData.role === 'superadmin' && !showEmailDomainError && formData.email.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <Info className="h-3 w-3" />
                          Superadmin requires @harriscomputer.com or @caretracker.com email
                        </p>
                      )}
                      {showEmailDomainError && (
                        <p className="text-xs text-red-600 mt-1">
                          Invalid domain. Use @harriscomputer.com or @caretracker.com
                        </p>
                      )}
                    </div>

                    {mode === 'edit' && (
                      <div className="mt-4 flex items-center">
                        <input
                          type="checkbox"
                          id="active"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <label htmlFor="active" className="ml-2 text-sm font-medium text-gray-700">
                          Active User
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Role Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                      Role Assignment
                    </h3>
                    <div>
                      <label htmlFor="user-modal-role" className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                      <select
                        id="user-modal-role"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        value={formData.role}
                        onChange={(e) => {
                          const newRole = e.target.value as UserRole;
                          const updates: Partial<User> = { role: newRole };
                          // Clear irrelevant assignment fields on role change
                          if (ENTERPRISE_ROLES.includes(newRole)) {
                            updates.organizationId = undefined;
                            updates.partnerId = undefined;
                          } else if (PARTNER_ROLES.includes(newRole)) {
                            updates.organizationId = undefined;
                            updates.enterpriseId = undefined;
                          } else if (ORG_ROLES.includes(newRole)) {
                            updates.enterpriseId = undefined;
                            updates.partnerId = undefined;
                          } else {
                            // superadmin
                            updates.organizationId = undefined;
                            updates.enterpriseId = undefined;
                            updates.partnerId = undefined;
                          }
                          setFormData(prev => ({...prev, ...updates}));
                        }}
                        required
                      >
                        {isSuperAdmin() && (
                          <optgroup label="Platform">
                            <option value="superadmin">Superadmin (Platform Only)</option>
                          </optgroup>
                        )}
                        <optgroup label="Enterprise">
                          <option value="enterprise_admin">Enterprise Admin</option>
                          <option value="enterprise_user">Enterprise User (Read Only)</option>
                        </optgroup>
                        <optgroup label="Channel Partner">
                          <option value="channel_partner_admin">Channel Partner Admin</option>
                          <option value="channel_partner_user">Channel Partner User (Read Only)</option>
                        </optgroup>
                        <optgroup label="Organization (Clinical)">
                          <option value="admin">Admin (Org Management)</option>
                          <option value="operator">Operator (Clinical Full Access)</option>
                          <option value="staff">Staff (Read Only)</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Superadmin Info Box */}
                    {formData.role === 'superadmin' && (
                      <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                        <p className="text-sm text-purple-700 flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          Superadmins operate at platform level and are not assigned to any organization or enterprise
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Enterprise Assignment - for enterprise roles */}
                  {showEnterpriseAssignment && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Enterprise Assignment
                      </h3>
                      <div>
                        <label htmlFor="user-modal-enterprise" className="block text-sm font-medium text-gray-700 mb-1">Enterprise *</label>
                        <select
                          id="user-modal-enterprise"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          value={formData.enterpriseId || ''}
                          onChange={(e) => setFormData({...formData, enterpriseId: e.target.value || undefined})}
                          required
                        >
                          <option value="">Select Enterprise</option>
                          {enterprises.map(ent => (
                            <option key={ent.id} value={ent.id}>{ent.name}</option>
                          ))}
                        </select>
                        {enterprises.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            No enterprises available. Create an enterprise first.
                          </p>
                        )}
                      </div>
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <Building2 className="h-4 w-4" />
                          {formData.role === 'enterprise_admin'
                            ? 'Enterprise Admins can manage channel partners and view aggregated data'
                            : 'Enterprise Users have read-only access to channel partner data'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Partner Assignment - for channel partner roles */}
                  {showPartnerAssignment && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Channel Partner Assignment
                      </h3>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Channel Partner *</label>
                        {!canSelectPartner ? (
                          // Searching partners needs platform.partners.view. Without it the list is
                          // always empty, so the assignment is shown rather than offered.
                          currentUser?.partnerId ? (
                            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-medium">
                              Your channel partner account
                            </div>
                          ) : (
                            // The column behind the claim is populated inconsistently, and there is
                            // nothing here to fill the field with, so say so rather than claim an
                            // account and refuse the save.
                            <div className="w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-md text-sm text-amber-800">
                              Your account is not linked to a channel partner, so partner roles
                              cannot be assigned. A platform administrator must set it first.
                            </div>
                          )
                        ) : (
                        <select
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          value={formData.partnerId || ''}
                          onChange={(e) => setFormData({...formData, partnerId: e.target.value || undefined})}
                          required
                        >
                          <option value="">Select Channel Partner</option>
                          {availablePartners.map(partner => (
                            <option key={partner.id} value={partner.id}>
                              {partner.name} {partner.isOwnerPartner ? '(Owner)' : `(${partner.type})`}
                            </option>
                          ))}
                        </select>
                        )}
                        {canSelectPartner && !isLoadingPartners && availablePartners.length === 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            No partners available. Create a channel partner first.
                          </p>
                        )}
                      </div>
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-sm text-blue-700 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {formData.role === 'channel_partner_admin'
                            ? 'Partner Admins can manage organizations and users under their partner'
                            : 'Partner Users have read-only access to partner organizations'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Organization Assignment - for clinical roles */}
                  {showOrgAssignment && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Organization Assignment
                      </h3>

                      {/* Partner Filter */}
                      {availablePartners.length > 0 && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filter by Partner
                            <span className="text-gray-400 font-normal ml-1">(optional)</span>
                          </label>
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            value={selectedPartnerId}
                            onChange={(e) => setSelectedPartnerId(e.target.value)}
                            disabled={isLoadingPartners}
                          >
                            <option value="">All Partners</option>
                            {availablePartners.map(partner => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name} {partner.isOwnerPartner ? '(Owner)' : `(${partner.type})`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Organization Selector */}
                      <div>
                        <label htmlFor="user-modal-organization" className="block text-sm font-medium text-gray-700 mb-1">Organization *</label>
                        <select
                          id="user-modal-organization"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          value={formData.organizationId}
                          onChange={(e) => setFormData({...formData, organizationId: e.target.value})}
                          required
                        >
                          <option value="">Select Organization</option>
                          {filteredOrganizations.map(org => (
                            <option key={org.id} value={org.id}>{org.name}</option>
                          ))}
                        </select>
                        {filteredOrganizations.length === 0 && selectedPartnerId && (
                          <p className="text-xs text-amber-600 mt-1">
                            No organizations found for this partner
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Practitioner Credentials - only for operator */}
                  {showPractitionerForm && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Practitioner Credentials
                      </h3>
                      <PractitionerCredentialsForm
                        credentials={formData.practitioner}
                        onChange={(credentials) => setFormData({ ...formData, practitioner: credentials })}
                        onValidation={(isValid, errors) => {
                          setPractitionerValid(isValid);
                          setPractitionerErrors(errors);
                        }}
                      />
                    </div>
                  )}

                  {/* Explicit grants — only meaningful for superadmins.
                      These permissions have `excludeFromWildcard: true` so
                      they aren't auto-granted by `platform.*`; each one must
                      be checked on per-user. The big read-only Permission
                      Matrix that used to live here was removed: it was
                      verbose, not actionable, and the role itself already
                      tells you the wildcard coverage. */}
                  {formData.role === 'superadmin' &&
                    permissions.some((p) => p.excludeFromWildcard) && (
                      <ExplicitGrantsEditor
                        availablePermissions={permissions.filter(
                          (p) => p.excludeFromWildcard,
                        )}
                        selected={formData.permissions ?? []}
                        onChange={(next) =>
                          setFormData((prev) => ({
                            ...prev,
                            permissions: next,
                          }))
                        }
                      />
                    )}
                </motion.div>
              </TabsContent>

              {/* Tab 2: Profile & Settings - hidden for superadmin */}
              {showProfileTab && (
                <TabsContent value="profile" className="mt-0">
                  <motion.div
                    key="profile-tab"
                    variants={tabContentVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.2 }}
                    className="space-y-6"
                  >
                    {/* Professional Profile Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Professional Profile
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="user-modal-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                          <Input
                            id="user-modal-title"
                            value={formData.profile?.title || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              profile: {...formData.profile!, title: e.target.value}
                            })}
                            placeholder="e.g., Physician, Nurse Practitioner"
                          />
                        </div>
                        <div>
                          <label htmlFor="user-modal-department" className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                          <Input
                            id="user-modal-department"
                            value={formData.profile?.department || ''}
                            onChange={(e) => setFormData({
                              ...formData,
                              profile: {...formData.profile!, department: e.target.value}
                            })}
                            placeholder="e.g., Cardiology"
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <label htmlFor="user-modal-phone-number" className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <Input
                          id="user-modal-phone-number"
                          type="tel"
                          value={formData.profile?.phoneNumber || ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            profile: {...formData.profile!, phoneNumber: e.target.value}
                          })}
                          placeholder="(555) 123-4567"
                        />
                      </div>

                      {/* Specialties */}
                      <div className="mt-4">
                        <label htmlFor="user-modal-specialties" className="block text-sm font-medium text-gray-700 mb-1">Specialties</label>
                        <div className="flex gap-2 mb-2">
                          <Input
                            id="user-modal-specialties"
                            value={specialtyInput}
                            onChange={(e) => setSpecialtyInput(e.target.value)}
                            placeholder="Add specialty"
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                          />
                          <Button type="button" onClick={addSpecialty} variant="outline">
                            Add
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {formData.profile?.specialties?.map((specialty, index) => (
                            <motion.span
                              key={index}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-sm"
                            >
                              {specialty}
                              <motion.button
                                type="button"
                                onClick={() => removeSpecialty(index)}
                                className="ml-2 text-purple-600 hover:text-purple-800"
                                whileHover={{ scale: 1.2 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                ×
                              </motion.button>
                            </motion.span>
                          ))}
                        </div>
                      </div>

                      {/* External EHR User ID - always shown when org is selected */}
                      {showExternalIdField && (
                        <div className="mt-4">
                          <label htmlFor="user-modal-ehr-user-id-external-id" className="block text-sm font-medium text-gray-700 mb-1">EHR User ID (External ID)</label>
                          <Input
                            id="user-modal-ehr-user-id-external-id"
                            value={formData.externalId || ''}
                            onChange={handleExternalIdChange}
                            placeholder="e.g., CT123456, PM789012"
                            maxLength={255}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Unique identifier for this user in the connected EHR system. Must be unique within this organization.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Preferences Section */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                        Preferences
                      </h3>

                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="emailNotifications"
                            checked={formData.preferences?.emailNotifications}
                            onChange={(e) => setFormData({
                              ...formData,
                              preferences: {...formData.preferences!, emailNotifications: e.target.checked}
                            })}
                            className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                          />
                          <label htmlFor="emailNotifications" className="ml-2 text-sm text-gray-700">
                            Email Notifications
                          </label>
                        </div>

                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label htmlFor="user-modal-language" className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                          <select
                            id="user-modal-language"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            value={formData.preferences?.language}
                            onChange={(e) => setFormData({
                              ...formData,
                              preferences: {...formData.preferences!, language: e.target.value}
                            })}
                          >
                            <option value="en-US">English (US)</option>
                            <option value="es-MX">Spanish (Mexico)</option>
                            <option value="es-ES">Spanish (Spain)</option>
                            <option value="fr-CA">French (Canada)</option>
                          </select>
                        </div>

                        <div>
                          <label htmlFor="user-modal-default-audio-quality" className="block text-sm font-medium text-gray-700 mb-1">Default Audio Quality</label>
                          <select
                            id="user-modal-default-audio-quality"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            value={formData.preferences?.defaultAudioQuality}
                            onChange={(e) => setFormData({
                              ...formData,
                              preferences: {...formData.preferences!, defaultAudioQuality: e.target.value as any}
                            })}
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </TabsContent>
              )}
            </div>
            {/* End of Scrollable Content Area */}
          </Tabs>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50">
            {/* whitespace-pre-line: a validation failure carries one line per reason, and without it
                they collapse onto one line with the bullets run together. */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm whitespace-pre-line"
                >
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex justify-end gap-3">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {isLoading && (
                    <motion.div
                      className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  )}
                  {isLoading ? 'Saving...' : mode === 'create' ? 'Create User' : mode === 'edit-invitation' ? 'Update Invitation' : 'Save Changes'}
                </Button>
              </motion.div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
